/**
 * Cloud Kitchen panel — filter toolbar, error banner, orders table.
 * Restaurant: status / search / delivery filters in toolbar.
 * Next day promo: Today / Tomorrow tabs; same toolbar filters when Today is selected.
 */

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { useTheme, useMediaQuery } from '@mui/material';
import CancelRounded from '@mui/icons-material/CancelRounded';

import InputBox from '../../atomic/inputbox/InputBox';
import {
  MenuProps,
  sharedInputSx,
  getStatusMenuProps,
} from '../Products/kitchenProductsConstants';
import { NEXT_DAY_PROMO_DAY_TABS, NEXT_DAY_PROMO_DAY_TODAY, STATUS_TABS } from './orderManagementConfig';
import { OrderManagementOrdersTable } from './OrderManagementOrdersTable';

const FILTER_FORM_CONTROL_SX = {
  ...sharedInputSx,
  '& .MuiSelect-select': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
  },
};

const DELIVERY_TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'home delivery', label: 'Home Delivery' },
  { value: 'pickup', label: 'Pickup' },
];

function StatusUnderlineTabs({ tabs, activeValue, onChange, tabCounts, colors }) {
  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3.5 },
        pt: 2,
        pb: 0,
        borderBottom: `1px solid ${colors.border}`,
        display: 'flex',
        alignItems: 'flex-end',
        gap: 0,
        overflowX: 'auto',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {tabs.map((tab) => {
        const active = activeValue === tab.value;
        const count = tabCounts[tab.value] ?? 0;
        return (
          <Box
            key={tab.value}
            onClick={() => onChange(tab.value)}
            sx={{
              px: 2,
              py: 1.25,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              fontFamily: active ? 'Inter Bold, sans-serif' : 'Inter Regular, sans-serif',
              fontWeight: active ? 600 : 400,
              fontSize: 13,
              color: active ? (colors.activeTabText || colors.ink) : (colors.inactiveTabText || colors.muted),
              whiteSpace: 'nowrap',
              borderBottom: active ? `2px solid ${colors.activeTabBorder || colors.ink}` : '2px solid transparent',
              mb: '-1px',
              transition: 'all 0.15s ease',
              '&:hover': { color: colors.ink },
            }}
          >
            {tab.label}
            {count > 0 && (
              <Box
                component='span'
                sx={{
                  px: 0.75,
                  py: 0.1,
                  borderRadius: '5px',
                  bgcolor: active ? (colors.tabBadgeBg || colors.ink) : (colors.inactiveTabBadgeBg || '#F3F4F6'),
                  color: active ? (colors.tabBadgeText || '#FFFFFF') : (colors.inactiveTabBadgeText || '#6B7280'),
                  fontFamily: 'Inter Bold, sans-serif',
                  fontSize: 10.5,
                  fontWeight: 700,
                  transition: 'all 0.15s',
                }}
              >
                {count}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function FilterToolbar({
  statusFilter,
  onStatusFilterChange,
  orderIdSearch,
  onOrderIdSearchChange,
  deliveryTypeFilter,
  onDeliveryTypeFilterChange,
  colors,
}) {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3.5 },
        py: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1.5,
        borderBottom: `1px solid ${colors.rowDivider}`,
        bgcolor: colors.headerBg,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', width: '100%' }}>
        <InputBox
          type='text'
          label='Search order # or customer'
          size='small'
          height='auto'
          sx={{
            flex: isCompact ? '1 1 100%' : '1 1 260px',
            minWidth: isCompact ? '100%' : 220,
            maxWidth: isCompact ? '100%' : 320,
            ...sharedInputSx,
          }}
          name='orderIdSearch'
          value={orderIdSearch}
          onChange={(e) => onOrderIdSearchChange(e.target.value)}
          inputProps={
            orderIdSearch
              ? {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        size='small'
                        aria-label='Clear search'
                        onClick={() => onOrderIdSearchChange('')}
                      >
                        <CancelRounded sx={{ fontSize: 16, color: '#3F3F6E' }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              : {}
          }
        />

        <FormControl
          size='small'
          sx={{
            minWidth: isCompact ? '100%' : 200,
            width: isCompact ? '100%' : 200,
            maxWidth: isCompact ? '100%' : 200,
            ...FILTER_FORM_CONTROL_SX,
          }}
        >
          <InputLabel id='om-status-select-label'>Status</InputLabel>
          <Select
            labelId='om-status-select-label'
            id='om-status-select'
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            label='Status'
            MenuProps={getStatusMenuProps(isCompact)}
          >
            {STATUS_TABS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          size='small'
          sx={{
            minWidth: isCompact ? '100%' : 160,
            ...FILTER_FORM_CONTROL_SX,
          }}
        >
          <InputLabel id='om-delivery-type-select-label'>Delivery type</InputLabel>
          <Select
            labelId='om-delivery-type-select-label'
            id='om-delivery-type-select'
            value={deliveryTypeFilter}
            onChange={(e) => onDeliveryTypeFilterChange(e.target.value)}
            input={<OutlinedInput label='Delivery type' />}
            MenuProps={MenuProps}
          >
            {DELIVERY_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
}

export function OrderManagementCloudKitchenPanel({
  variant = 'restaurant',
  statusFilter,
  onStatusFilterChange,
  tabCounts,
  nextDayPromoDayFilter,
  onNextDayPromoDayFilterChange,
  nextDayPromoTabCounts,
  orderIdSearch,
  onOrderIdSearchChange,
  deliveryTypeFilter,
  onDeliveryTypeFilterChange,
  loadError,
  tableProps,
  colors,
}) {
  const isNextDayPromo = variant === 'nextDayPromo';
  const showRestaurantFilters =
    !isNextDayPromo || nextDayPromoDayFilter === NEXT_DAY_PROMO_DAY_TODAY;

  return (
    <>
      {isNextDayPromo && (
        <StatusUnderlineTabs
          tabs={NEXT_DAY_PROMO_DAY_TABS}
          activeValue={nextDayPromoDayFilter}
          onChange={onNextDayPromoDayFilterChange}
          tabCounts={nextDayPromoTabCounts}
          colors={colors}
        />
      )}

      {showRestaurantFilters && (
        <FilterToolbar
          statusFilter={statusFilter}
          onStatusFilterChange={onStatusFilterChange}
          orderIdSearch={orderIdSearch}
          onOrderIdSearchChange={onOrderIdSearchChange}
          deliveryTypeFilter={deliveryTypeFilter}
          onDeliveryTypeFilterChange={onDeliveryTypeFilterChange}
          colors={colors}
        />
      )}

      {loadError && (
        <Box sx={{ px: { xs: 2, sm: 3.5 }, pt: 2 }}>
          <Box
            sx={{
              p: 2,
              bgcolor: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <CancelRounded sx={{ fontSize: 18, color: '#dc2626', flexShrink: 0 }} />
            <Typography sx={{ color: '#dc2626', fontFamily: 'Inter Regular, sans-serif', fontSize: 14 }}>
              {loadError}
            </Typography>
          </Box>
        </Box>
      )}

      <OrderManagementOrdersTable {...tableProps} colors={colors} />
    </>
  );
}
